import { StandardSensors } from '../constants/sensors.constants';

/** @returns {Array<{ text: string, value: number }>} */
export function mapOrganisationSitesToOptions(sites) {
  return (sites || [])
    .filter(s => s.active !== false)
    .map(s => ({ text: s.name, value: s.id }));
}

export function resolveSiteAccountId({ details, sensor, currentAccount }) {
  if (details && details.account_id != null) return details.account_id;
  if (sensor && sensor.account_id != null) return sensor.account_id;
  if (currentAccount && currentAccount.id != null) return currentAccount.id;
  return '';
}

export function resolveOrganisationId({ details, sensor, currentAccount }) {
  if (details && details.organisation_id != null) return details.organisation_id;
  if (details && details.organisation && details.organisation.id != null) {
    return details.organisation.id;
  }
  if (sensor && sensor.organisation_id != null) return sensor.organisation_id;
  if (sensor && sensor.organisation && sensor.organisation.id != null) {
    return sensor.organisation.id;
  }
  if (currentAccount && currentAccount.organisation_id != null) {
    return currentAccount.organisation_id;
  }
  if (
    currentAccount &&
    currentAccount.organisation &&
    currentAccount.organisation.id != null
  ) {
    return currentAccount.organisation.id;
  }
  return null;
}

function hasMachineAssignment(value) {
  return value != null && value !== '';
}

/**
 * True when the device is assigned to a machine (list row, detail state, or local edit state).
 * Uses list props (sensor.machine / machine_id) so stale detail state cannot allow site transfer.
 */
export function hasDeviceMachineAssociation({
  sensor,
  details,
  associatedMachineId,
} = {}) {
  if (hasMachineAssignment(associatedMachineId)) return true;
  if (sensor) {
    if (hasMachineAssignment(sensor.machine_id)) return true;
    if (hasMachineAssignment(sensor.machine)) return true;
    if (hasMachineAssignment(sensor.location_id)) return true;
    if (hasMachineAssignment(sensor.location)) return true;
  }
  if (details) {
    if (hasMachineAssignment(details.machine)) return true;
    if (hasMachineAssignment(details.machine_id)) return true;
    if (hasMachineAssignment(details.location)) return true;
    if (hasMachineAssignment(details.location_id)) return true;
  }
  return false;
}

function isPortSensorOnMachine(sensor) {
  return hasMachineAssignment(sensor.machine_id) || hasMachineAssignment(sensor.machine);
}

/**
 * True when a transmitter or any sensor on its ports is assigned to a machine.
 */
export function hasTransmitterMachineAssociation({ sensor, details } = {}) {
  if (hasDeviceMachineAssociation({ sensor, details })) return true;
  if (sensor && hasMachineAssignment(sensor.machines)) return true;
  if (details && Array.isArray(details.ports)) {
    return details.ports.some(port =>
      (port.sensors || []).some(isPortSensorOnMachine)
    );
  }
  return false;
}

// Only whole units transfer (Mote/Tx/VSX); individual TxSensors would drag their parent Tx.
export function isReassignableDeviceType(type) {
  return StandardSensors.includes(type) || type === 'VSX';
}

/**
 * True when sensors are still plugged into the transmitter's ports, whether or
 * not they are on a machine. Transferring the Tx alone moves only its own row,
 * so the sensors would keep the old account while still wired to it.
 */
export function hasTransmitterAttachedSensors({ details } = {}) {
  if (!details || !Array.isArray(details.ports)) return false;
  return details.ports.some(port => (port.sensors || []).length > 0);
}

/**
 * True when the VSX is still sitting on a transmitter's ports.
 * getSensorDetails normalises the link to `tx` (`tx: res.tx_id`), so read that
 * first; the list row carries `tx`/`tx_id`. Same resolution order as
 * VectorSensor.getReassignTransmitterId.
 */
export function isSensorGroupAttachedToTx({ details, sensor } = {}) {
  if (details && details.tx != null) return true;
  if (details && details.tx_id != null) return true;
  if (sensor && sensor.tx != null) return true;
  if (sensor && sensor.tx_id != null) return true;
  return false;
}

/**
 * Wiring that must be undone before a site transfer, independent of machines.
 * Mirrors the backend guard on transfer-device, so the UI blocks with the same
 * rule the API enforces instead of letting the request fail.
 */
export function hasReassignBlockingWiring({ type, details, sensor } = {}) {
  if (type === 'Transmitter') return hasTransmitterAttachedSensors({ details });
  if (type === 'VSX') return isSensorGroupAttachedToTx({ details, sensor });
  return false;
}

/**
 * Group a selection by why each device cannot move site. Each device is
 * reported under one reason only, so the hint never repeats a serial.
 */
export function collectReassignBlockers(
  selected,
  { isAssociated, getDetailRow } = {}
) {
  const notTransferable = [];
  const onMachine = [];
  const wired = [];
  const attachedToTx = [];

  (selected || []).forEach((device) => {
    const serial = device.serial_number;
    if (!isReassignableDeviceType(device.type)) {
      notTransferable.push(serial);
      return;
    }
    const details = getDetailRow ? getDetailRow(device) : null;
    // A Tx holds sensors; a VSX sits on someone else's port. Same invariant,
    // opposite ends, so they need different remedies in the hint.
    // The Tx check comes first deliberately: a Tx is only ever on a machine
    // via the sensors on its ports, so unassigning them is the remedy either
    // way -- and it is what the Dissociate control on that group actually does.
    if (device.type === 'Transmitter'
      && hasTransmitterAttachedSensors({ details })) {
      wired.push(serial);
      return;
    }
    if (isAssociated && isAssociated(device)) {
      onMachine.push(serial);
      return;
    }
    if (device.type === 'VSX'
      && isSensorGroupAttachedToTx({ details, sensor: device })) {
      attachedToTx.push(serial);
    }
  });

  return { notTransferable, onMachine, wired, attachedToTx };
}

export function hasReassignBlockers(blockers) {
  if (!blockers) return false;
  return (
    blockers.notTransferable.length > 0
    || blockers.onMachine.length > 0
    || blockers.wired.length > 0
    || blockers.attachedToTx.length > 0
  );
}

/**
 * Explain the first blocking reason, naming the devices responsible. Without
 * the serials a mixed selection gives no clue which device holds the gate shut.
 */
export function buildReassignDisabledHint(blockers, t) {
  if (!blockers || !t) return '';

  if (blockers.notTransferable.length > 0) {
    return t('sensors.reassignSite.disabledHintNotTransferableDevices', {
      serialNumbers: blockers.notTransferable.join(', '),
      defaultValue: 'Cannot be reassigned on their own: {{serialNumbers}}. Only whole devices (Motes, Transmitters, or VSX units) can move to a different site.'
    });
  }

  if (blockers.onMachine.length > 0) {
    return t('sensors.reassignSite.disabledHintDevices', {
      serialNumbers: blockers.onMachine.join(', '),
      defaultValue: 'Still assigned to a machine: {{serialNumbers}}. Dissociate every selected device to reassign them to a different site.'
    });
  }

  if (blockers.wired.length > 0) {
    return t('sensors.reassignSite.disabledHintWired', {
      serialNumbers: blockers.wired.join(', '),
      defaultValue: 'Sensors are still assigned to the ports of: {{serialNumbers}}. Unassign them before reassigning to a different site.'
    });
  }

  if (blockers.attachedToTx.length > 0) {
    return t('sensors.reassignSite.disabledHintOnTx', {
      serialNumbers: blockers.attachedToTx.join(', '),
      defaultValue: 'Still assigned to a TX port: {{serialNumbers}}. Unassign them from the TX before reassigning to a different site.'
    });
  }

  return t('sensors.reassignSite.disabledHint');
}

// Map a selected device to transfer-endpoint params. Selection is gated to Mote/Tx/VSX upstream.
export function resolveDeviceTransferParams(dev, row) {
  if (!dev) return null;
  if (dev.type === 'VSX') {
    const id = dev.id != null ? dev.id : row && row.id;
    return id != null ? { device_type: 'vsx', device_id: id } : null;
  }
  if (dev.type === 'Transmitter') {
    const id = dev.id != null ? dev.id : row && row.id;
    return id != null ? { device_type: 'tx', device_id: id } : null;
  }
  const moteId = dev.id != null ? dev.id : row && row.id;
  return moteId != null ? { device_type: 'mote', device_id: moteId } : null;
}
